import { useCallback } from "react";
import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { ViewStateInspectionView } from "../../../other/feature/views/widget/ViewStateInspectionView.jsx";
import SwivuModal from "./SwivuModal.jsx";
import { MessageDialog } from "@bryntum/schedulerpro";
import { showConfirmationDialog } from "../../../other/utils/utils.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { VIEW_STATE_PIITS_UPDATE_LISTENER, ViewFolderTypes } from "../../../other/constants/OtherKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";

class SwivuViewInspectorEditor extends EditorBase {
  static get $name() {
    return "swivuviewinspectoreditor";
  }

  static get type() {
    return WIDGET_KEYS.swivuViewInspectorEditor;
  }

  static get configurable() {
    return {
      stateId: PERSISTENCE_STATE_KEYS.viewInspectorEditor,
      title: "View Editor",
      minWidth: "891px",
      minHeight: "570px",
      maxHeight: "100vh",
      closable: true,
      autoClose: false,
      modal: true,
      draggable: true,
      defaultFocus: w => w.ref === 'bbar',
      cls: "minimal-gradient-editor editor-header-text-left-aligned swivu-view-inspector-editor [&_.b-panel-body-wrap]:!mb-0 p-[1em]",
      header: {
        cls: "p-0 pb-[1em] [&_.header-close-esc-button]:!right-[1.3em]",
      },
      contentElementCls: "p-0",
      items: {
        container: {
          type: "container",
          cls: "gap-[1em]",
          width: "100%",
          height: "100%",
          items: {
            viewInspection: {
              type: ViewStateInspectionView.type,
              flex: "0 0 40%",
            },
            viewDetails: {
              type: "container",
              contentElementCls: "gap-0",
              cls: "swivu-view-inspector-field-container [&_.b-field-inner]:!py-0",
              flex: 1,
              defaults: {
                onChange: "up.onRecordChange",
                labelWidth: "12em",
                labelCls: "pl-[1em] !me-0 text-[1em]",
                cls: "swivu-view-inspector-field"
              },
              items: {
                viewName: {
                  type: "textField",
                  label: "Name",
                  name: "name",
                  onInput: "up.handleSaveButtonState"
                },
                viewDescription: {
                  type: "textField",
                  label: "Description",
                  name: "viewDescription",
                },
                columnField: {
                  type: "combo",
                  name: "folderId",
                  label: "Column",
                  editable: false,
                  items: [],
                  displayField: "name",
                  valueField: "id",
                  onChange: "up.onColumnFieldChange",
                },
                columnManager: {
                  type: "combo",
                  name: "columnManager",
                  label: "Column Manager",
                  items: [],
                  placeholder: "See Column Views",
                  displayField: "name",
                  valueField: "id",
                  editable: false,
                  triggers: {
                    settings: {
                      cls: "fa-solid fa-pencil !text-[0.7em] cursor-pointer pt-[0.313em]",
                      onClick: "onColumnManagerSettingsClick",
                      weight: 1 // Lower weight should place it before the caret
                    }
                  },
                  picker: {
                    type: "list",
                    cls: "[&_li.b-active]:!bg-[transparent] [&_li:not(:last-child)]:!cursor-auto [&_li:last-child]:!bg-[#ebebeb] [&_li:last-child]:dark:!bg-[#716767]",
                    listeners: {
                      beforeItem: "up.onColumnManagerPickerBeforeShow",
                    }
                  }
                },
                viewIcon: {
                  type: WIDGET_KEYS.fieldIcon,
                  label: "Icon",
                  displayField: "tagValue",
                  valueField: "id",
                  name: "iconId",
                  editable: false
                },
                viewIconColor: {
                  type: "swivucolorfield",
                  name: "color",
                  label: "Icon Color",
                  cls: "swivucolorfield swivu-view-inspector-field",
                  canRemoveColor: true,
                  flex: "1 0 100%",
                  canDisplayColorIcon: true,
                  onChange: "up.onSwivuColorChange"
                },
                shareToHq: {
                  label: "Share To HQ",
                  name: "isShareToHQ",
                  type: "checkbox"
                },
                shareToWorkforce: {
                  label: "Share To Workforce",
                  name: "isShareToWorkforce",
                  type: "checkbox"
                },
                shareToPublic: {
                  label: "Share To Public",
                  name: "isShareToPublic",
                  type: "checkbox"
                }
              }
            }
          }
        }
      },
      bbar: {
        contentElementCls: "!p-0",
        items: {
          bbarContainer: {
            type: "container",
            width: "100%",
            layoutStyle: {
              justifyContent: "space-between",
              alignItems: "center"
            },
            items: {
              bbarTextContainer: {
                type: "container",
                layoutStyle: {
                  flexDirection: "column",
                },
                items: {
                  bbarText: {
                    cls: "[&_span]:!p-0 text-[#6c6b6b]",
                    type: "displayField",
                    flex: "auto"
                  },
                  bbarPIITSInfoLabel: {
                    type: "displayField",
                    cls: "[&_span]:!p-0 text-[#E98906]",
                    hidden: true
                  },
                }
              },
              buttonContainer: {
                type: "container",
                cls: "gap-2",
                items: {
                  deleteButton: {
                    type: "button",
                    text: "Delete",
                    cls: "bg-transparent !rounded-lg dark:text-white",
                    onClick: "up.onRemoveView"
                  },
                  saveButton: {
                    type: "button",
                    text: "Save View",
                    cls: "bg-transparent !rounded-lg dark:text-white",
                    onClick: "up.onSaveView",
                    disabled: true
                  }
                }
              }
            }
          }
        }
      },
      listeners: {
        hide: "onEditorHide"
      }
    };
  }

  onColumnFieldChange({ value, userAction }) {
    if (userAction) {
      this.updateColumnManagerData(value);
      this.handleSaveButtonState();
    }
  }

  updateColumnManagerData(folderId) {
    const { columnManager } = this.widgetMap;

    // Get views that belong to the selected folder
    const folderViews = this.context.project.viewsStore.query((view) => {
      return folderId === view.folderId;
    }, true);

    const folderName = this.context.project.viewsFolderStore.getById(folderId)?.name;

    // Transform folder views to plain objects and add "Manage Columns"
    columnManager.items = [
      ...folderViews.map((view) => ({
        id: view.id,
        name: view.name
      })),
      {
        id: "Manage Columns",
        name: "Manage Column"
      }
    ];

    columnManager.placeholder = folderName;
    this.currentSelectedColumnInCombo = folderId;
  }

  onColumnManagerSettingsClick() {
    const folderRecord = this.context.project.viewsFolderStore.getById(this.currentSelectedColumnInCombo);
    this.context.editorHandler.openEditor(EDITOR_KEYS.viewOrganizerEditor, null, {folderRecord}, {
      useExisting: true,
      by: "recordType"
    });
  }

  onColumnManagerPickerBeforeShow({record, source}) {
    if (record.id === "Manage Columns") {
      source.owner.hidePicker();
      this.onColumnManagerSettingsClick();
      return false; // Prevent the default behavior of the item click
    }
    // Prevent the default behavior of the item click
    return false;
  }

  loadRecord(record, context, otherParams) {
    const { viewInspection, viewIcon, columnField, columnManager } = this.widgetMap;

    columnField.store.data = context.project.viewsFolderStore.query((folder) => {
      return {
        id: folder.id,
        name: folder.name,
      }
    }, true)

    super.editRecord(record, context, otherParams);

    columnManager.triggers.settings.onClick = () => {
      this.onColumnManagerSettingsClick();
    }

    viewInspection.loadRecord(record, context, otherParams);
    viewIcon.store.data = context.project.resourceTagValues.query(
      (icon) => icon.tagSet === "Icon",
      true
    );
    this.record = record;
    this.otherParams = otherParams;
    this.context = context;
    this.currentSelectedColumnInCombo = record.folderId;

    this.updateColumnManagerData(record.folderId);

    this.handleView();
    this.updateBbarText();
    this.handleSaveButtonState();
    this.changeCrossToEsc();
  }

  onRecordChange({ userAction }) {
    if (userAction) {
      this.handleSaveButtonState();
    }
  }

  onSwivuColorChange({ value, ...rest }) {
    const { viewIcon, viewName } = this.widgetMap;
    const iconEl = viewIcon.element.querySelector(".icon-label");
    iconEl.style.color = value;
    viewName.element.style.color = value;
    this.onRecordChange(rest);
  }

  handleSaveButtonState() {
    const { viewName, viewDescription, shareToHq, shareToPublic, shareToWorkforce, viewIcon, viewIconColor, columnField, saveButton } = this.widgetMap;
    const selectedView = this.record;
    const haveFieldsChanged = [viewName, viewDescription, shareToHq, shareToPublic, shareToWorkforce, viewIcon, viewIconColor, columnField].some(field => selectedView[field.name] !== field.value);

    const isViewNameEmpty = !viewName.value?.trim();
    saveButton.disabled = isViewNameEmpty || !((selectedView.isOwnView && selectedView.hasPIITS) || haveFieldsChanged);

    if (selectedView.isOwnView && !this.swivuSaveButtonStateListener) {
      this.swivuSaveButtonStateListener = this.context.project.userPreferencesStore.on(VIEW_STATE_PIITS_UPDATE_LISTENER, () => {
        saveButton.disabled = !selectedView.hasPIITS;
      });
    }

    this.onViewNameFieldValidation({ source: viewName });

  }

  onEditorHide() {
    if (this.swivuSaveButtonStateListener) this.swivuSaveButtonStateListener();
  }

  handleView() {
    const {
      viewName,
      viewDescription,
      deleteButton,
      shareToHq,
      shareToPublic,
      shareToWorkforce,
      columnField,
      bbarPIITSInfoLabel
    } = this.widgetMap;

    const { isSaveAsView } = this.otherParams;
    const selectedView = this.record;
    const isDefault = selectedView.isDefaultView;
    const isAdmin = this.context.user.can(GRANTS.grant_one);
    const isOwn = selectedView.isOwnView;

    const isDeletable = !(!isDefault && (isAdmin || isOwn));
    const isShareable = !(isSaveAsView || isAdmin);

    if (isSaveAsView) {
      viewName.value = '';
      viewName.focus();
      viewName.disabled = false;
      this.handleSaveButtonState();

      const moreFolder = this.context.project.viewsFolderStore.find((folder) => folder.type === ViewFolderTypes.more);
      if (moreFolder) {
        columnField.value = moreFolder.id;
        this.updateColumnManagerData(moreFolder.id);
      }
    } else {
      viewName.value = selectedView.name;
      viewName.disabled = true;
    }
    viewDescription.value = selectedView.viewDescription || '';
    deleteButton.disabled = isDeletable;
    shareToHq.disabled = isShareable;
    shareToPublic.disabled = isShareable;
    shareToWorkforce.disabled = isShareable;

    if (!selectedView.isOwnView) {
      bbarPIITSInfoLabel.hidden = false;
      viewDescription.disabled = true;
      columnField.disabled = true;
      bbarPIITSInfoLabel.value = `${selectedView.creatorUserName} is the only user that can save changes to this view`;
    }

    if (this.widgetMap.createdBy) {
      this.widgetMap.createdBy.value = selectedView?.creatorUserName
    }
  }

  handleRecordUpdate(record) {
    const {
      viewName,
      shareToHq,
      shareToPublic,
      shareToWorkforce,
      viewIcon,
      viewIconColor,
      columnField,
      viewDescription
    } = this.widgetMap;

    record.name = viewName.value;
    record.iconId = viewIcon.value;
    record.color = viewIconColor.value;
    record.folderId = columnField.value;
    record.isShareToHQ = shareToHq.value;
    record.isShareToPublic = shareToPublic.value;
    record.isShareToWorkforce = shareToWorkforce.value;
    record.viewDescription = viewDescription.value;

    /* If all share checkboxes are marked uncheck and view is not own then remove view from user's store */
    if ([record.isShareToHQ, record.isShareToPublic, record.isShareToWorkforce].filter(shareViewValue => shareViewValue).length === 0 && !record.isOwnView) {
      this.onUnShareSomeoneElseView();
    }
  }

  onViewNameFieldValidation({ source }) {
    if (source.value === this.record.name) {
      source.setError("Name of view must not match with the one being saved from");
    } else {
      source.clearError();
    }
  }

  onSaveView() {
    const isViewSelected = this.record.id === this.context.viewManager.renderState.selectedView.id;
    let isViewUpdated = true;
    if (this.otherParams.isSaveAsView) {
      this.onSaveViewAs()
    } else {
      this.handleRecordUpdate(this.record);
      if (this.record.isOwnView && isViewSelected) {
        isViewUpdated = this.context.viewManager.updateView(
          this.record.id,
          this.record
        );
      }

      if (isViewUpdated) {
        MessageDialog.alert({
          title: "Success",
          message: "View updated successfully."
        }).finally(() => {
          this.close();
        });
      } else {
        MessageDialog.alert({
          title: "Failed",
          message: "Unable to update the view."
        });
      }
    }
  }

  onRemoveView() {
    const user = this.context.user;
    const viewToDelete = this.record;
    if (viewToDelete) {
      if (viewToDelete.isDefaultView) {
        MessageDialog.alert({
          title: "View Delete Not Allowed",
          message: "Swivu views cannot be deleted."
        });
      } else if (
        viewToDelete.isSharedView &&
        !user.can(GRANTS.grant_one) &&
        viewToDelete.creatorUserId !== user.id
      ) {
        MessageDialog.alert({
          title: "View Share Not Allowed",
          message: `Shared View created by ${viewToDelete.creatorUserName}. Only creator of this shared view or a board admin can delete this view.`
        });
      } else {
        showConfirmationDialog({
          title: "Delete View",
          message: `Are you sure, You want to delete a view called "${viewToDelete.name}"?`,
          confirmBtnText: "Delete"
        }).then((isConfirmedDelete) => {
          if (isConfirmedDelete) {
            this.context.viewManager.deleteView(viewToDelete);
            this.close();
          }
        });
      }
    }
  }

  updateBbarText() {
    const { bbarText } = this.widgetMap;
    bbarText.value = `Created by ${this.record.isDefaultView ? "Swivu" : this.record.creatorUserName}`;
  }

  onSaveViewAs() {
    const newRecord = this.record.copy();
    newRecord.isDefaultView = false;
    this.handleRecordUpdate(newRecord);
    const { viewManager } = this.context;
    const isViewCreated = viewManager.createView(newRecord);
    if (isViewCreated) {
      MessageDialog.alert({
        title: "Success",
        message: "View saved successfully."
      }).finally(() => {
        this.close();
      });
    } else {
      MessageDialog.alert({
        title: "Failed",
        message: "Unable to save view."
      });
    }
  }

  onUnShareSomeoneElseView() {
    const { record } = this;
    // You might ask why we are removing the view from store after the sync request?
    // The reason to it was that immediately removing the record from store after the record update above cause the bryntum to not initiating the sync call as it doesn't find record in store; bryntum not make update immediately and make project changes after a certain time
    const { project, viewManager } = this.context;
    project.on({
      sync: () => {
        if (!record.isOwnView) {
          const dataToApply = { removed: [{ id: record.id }] };
          project.viewsStore.applyChangeset(dataToApply);
          viewManager.selectDefaultViewOfCurrentView();
        }
      },
      thisObj: this,
      once: true,
    });
  }
}

SwivuViewInspectorEditor.initClass();

const SwivuViewInspectorPopup = ({
  context,
  record,
  onClose,
  isShow,
  editorInstance
}) => {
  const onShowHandler = useCallback(
    (editor, otherParams = {}) => {
      editor.project = context.project;
      const { viewRecord, canUseRecordViewState, isSaveAsView } = record;
      editor.loadRecord(viewRecord, context, { ...otherParams, canUseRecordViewState, isSaveAsView });
      editorInstance.editorRef = editor;
    },
    [context, editorInstance, record]
  );

  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuViewInspectorEditor}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  );
};

export default SwivuViewInspectorPopup;
